
import { useState, useRef } from 'react';

interface WheelOfFortuneProps {
  onPrizeWon: (prize: string) => void;
}

const prizes = [
  { label: '10 звезд', stars: 10, color: '#FF6B6B' },
  { label: '50 звезд', stars: 50, color: '#4ECDC4' },
  { label: '100 звезд', stars: 100, color: '#FFD93D' },
  { label: '25 звезд', stars: 25, color: '#95E1D3' },
  { label: '200 звезд', stars: 200, color: '#F38181' },
  { label: '75 звезд', stars: 75, color: '#AA96DA' },
  { label: '500 звезд', stars: 500, color: '#FCBAD3' },
  { label: '150 звезд', stars: 150, color: '#A8E6CF' },
  { label: '1000 звезд', stars: 1000, color: '#FFD700' },
  { label: '300 звезд', stars: 300, color: '#FF8B94' }
];

export default function WheelOfFortune({ onPrizeWon }: WheelOfFortuneProps) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const wheelRef = useRef<HTMLDivElement>(null);

  const spinWheel = () => {
    if (isSpinning) return;

    setIsSpinning(true);

    const randomIndex = Math.floor(Math.random() * prizes.length);
    const segmentAngle = 360 / prizes.length;
    const baseRotation = 360 * 5;
    const targetRotation = baseRotation + (360 - (randomIndex * segmentAngle + segmentAngle / 2));
    
    setRotation(rotation + targetRotation);

    setTimeout(() => {
      setIsSpinning(false);
      onPrizeWon(prizes[randomIndex].label);
    }, 4000);
  };

  return (
    <div className="relative flex flex-col items-center">
      <div className="relative w-full max-w-[90vw] md:max-w-[500px] aspect-square">
        <div 
          ref={wheelRef}
          className="absolute inset-0 rounded-full shadow-2xl"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
          }}
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            {prizes.map((prize, index) => {
              const angle = (360 / prizes.length) * index;
              const nextAngle = (360 / prizes.length) * (index + 1);
              const startAngle = (angle - 90) * (Math.PI / 180);
              const endAngle = (nextAngle - 90) * (Math.PI / 180);
              
              const x1 = 100 + 100 * Math.cos(startAngle);
              const y1 = 100 + 100 * Math.sin(startAngle);
              const x2 = 100 + 100 * Math.cos(endAngle);
              const y2 = 100 + 100 * Math.sin(endAngle);

              const textAngle = angle + (360 / prizes.length) / 2;
              const textRadius = 65;
              const textX = 100 + textRadius * Math.cos((textAngle - 90) * (Math.PI / 180));
              const textY = 100 + textRadius * Math.sin((textAngle - 90) * (Math.PI / 180));

              return (
                <g key={index}>
                  <path
                    d={`M 100 100 L ${x1} ${y1} A 100 100 0 0 1 ${x2} ${y2} Z`}
                    fill={prize.color}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text
                    x={textX}
                    y={textY}
                    fill="white"
                    fontSize="8"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                  >
                    {prize.label}
                  </text>
                  <text
                    x={textX}
                    y={textY - 8}
                    fill="white"
                    fontSize="12"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${textAngle}, ${textX}, ${textY - 8})`}
                  >
                    ⭐
                  </text>
                </g>
              );
            })}
            <circle cx="100" cy="100" r="15" fill="white" stroke="#333" strokeWidth="3" />
          </svg>
        </div>

        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="w-0 h-0 border-l-[15px] md:border-l-[20px] border-r-[15px] md:border-r-[20px] border-t-[30px] md:border-t-[40px] border-l-transparent border-r-transparent border-t-white drop-shadow-lg"></div>
        </div>
      </div>

      <button
        onClick={spinWheel}
        disabled={isSpinning}
        className="mt-6 md:mt-8 px-8 md:px-12 py-3 md:py-4 bg-white text-purple-600 rounded-full font-bold text-lg md:text-xl shadow-2xl hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 whitespace-nowrap"
      >
        {isSpinning ? (
          <span className="flex items-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>
            Крутится...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <i className="ri-refresh-line"></i>
            Крутить колесо
          </span>
        )}
      </button>
    </div>
  );
}
